import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { MdClose } from 'react-icons/md';
import { VideoPlayer } from 'components';
import { youtubeNoCookieUrlFromInput } from 'utils/youtube';
import * as Ui from './MediaPreviewModal.styles';

const MediaPreviewModal = ({
  isOpen,
  onClose,
  url,
  title,
  subtitle,
  mediaType,
  imageAlt,
  onMediaError,
  ariaLabel = 'Close preview',
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen || !url) return null;

  const isImage = mediaType === 'image';
  const youtubeEmbedUrl =
    mediaType === 'video' ? youtubeNoCookieUrlFromInput(url, 'autoplay=1&rel=0') : null;

  return createPortal(
    <Ui.PreviewBackdrop onClick={onClose}>
      <Ui.PreviewContainer onClick={(e) => e.stopPropagation()} $isImage={isImage}>
        <Ui.PreviewHeader>
          <Ui.PreviewHeaderText>
            {title && <Ui.PreviewTitle>{title}</Ui.PreviewTitle>}
            {subtitle && <Ui.PreviewSubTitle>{subtitle}</Ui.PreviewSubTitle>}
          </Ui.PreviewHeaderText>
          <Ui.PreviewClose type="button" onClick={onClose} aria-label={ariaLabel}>
            <MdClose />
          </Ui.PreviewClose>
        </Ui.PreviewHeader>
        <Ui.PreviewMedia>
          {mediaType === 'image' ? (
            <Ui.PreviewImage src={url} alt={imageAlt} onError={onMediaError} />
          ) : youtubeEmbedUrl ? (
            <Ui.PreviewIframe
              src={youtubeEmbedUrl}
              title={subtitle || title || 'Video preview'}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              onError={onMediaError}
            />
          ) : (
            <VideoPlayer src={url} autoPlay onError={onMediaError} />
          )}
        </Ui.PreviewMedia>
      </Ui.PreviewContainer>
    </Ui.PreviewBackdrop>,
    document.body,
  );
};

MediaPreviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  url: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  mediaType: PropTypes.oneOf(['video', 'image']),
  imageAlt: PropTypes.string,
  onMediaError: PropTypes.func,
  ariaLabel: PropTypes.string,
};

MediaPreviewModal.defaultProps = {
  url: null,
  title: null,
  subtitle: null,
  mediaType: 'video',
  imageAlt: '',
  onMediaError: undefined,
  ariaLabel: 'Close preview',
};

export default MediaPreviewModal;
